// Pricing Section - Fixed plan cards (Free, Pro, Max)
// Follows the Section, Component & Manager System architecture

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import { PricingManager } from './managers/pricing-manager';
import { useManager } from '../../core';
import { useSectionPayload, navigationManager } from '../../managers/navigation-manager';
import { useAuth } from '../../contexts/AuthContext';
import { PlanCard } from './components/PlanCard';
import { ProductCard } from './components/ProductCard';
import { CreditPackCard } from './components/CreditPackCard';
import { LocalCardEditModal } from './components/LocalCardEditModal';
import { ReactivateSubscriptionModal } from './components/ReactivateSubscriptionModal';
import { PlanSwitchModal } from './components/PlanSwitchModal';
import { CancelToFreeModal } from './components/CancelToFreeModal';

const PLAN_IDS = ['free', 'pro', 'max'];

const LOCAL_PRODUCT_DEFAULTS = {
  uniqueId: 'local',
  displayName: 'Local',
  description: '- Run locally\n- Full control',
  tiers: [{ id: 'basic', displayName: 'Basic', description: '', credits: 0, price: 0 }],
  discount: 0,
};

export function PricingSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { manager, isInitialized } = useManager(PricingManager);
  const { payload } = useSectionPayload();
  const { refreshUser, refreshCredits, user, isAuthenticated } = useAuth();
  const isAdminUser = user?.role === 'admin';
  const [reactivateModal, setReactivateModal] = useState({ isOpen: false, data: null });
  const [planSwitchModal, setPlanSwitchModal] = useState({ isOpen: false, data: null });
  const [cancelToFreeModal, setCancelToFreeModal] = useState({ isOpen: false, data: null });
  const [localCardEditModal, setLocalCardEditModal] = useState(false);
  const [isBlockingUI, setIsBlockingUI] = useState(false);
  const [blockingMessage, setBlockingMessage] = useState('Working...');

  const localConfig = manager.getLocalCardConfig();
  const localProduct = {
    ...LOCAL_PRODUCT_DEFAULTS,
    displayName: localConfig?.displayName ?? LOCAL_PRODUCT_DEFAULTS.displayName,
    description: localConfig?.description ?? LOCAL_PRODUCT_DEFAULTS.description,
    tiers: [{
      ...LOCAL_PRODUCT_DEFAULTS.tiers[0],
      price: typeof localConfig?.price === 'number' ? localConfig.price : 0,
    }],
  };

  const handlePlanSelect = useCallback(async (planData) => {
    // Unauthenticated: redirect to auth, then return here with payload to complete purchase
    if (!isAuthenticated) {
      navigationManager.requireAuth('/pricing', { action: 'purchase_plan', planData });
      return;
    }

    setBlockingMessage('Starting checkout...');
    setIsBlockingUI(true);
    try {
      const result = await manager.handlePlanSelect(planData);
      if (result && typeof result === 'object') {
        if (result.action === 'reactivate') {
          setIsBlockingUI(false);
          setReactivateModal({
            isOpen: true,
            data: {
              message: result.message,
              subscriptionId: result.subscriptionId,
              planData,
            },
          });
          return;
        }
        if (result.action === 'planSwitch') {
          setIsBlockingUI(false);
          setPlanSwitchModal({
            isOpen: true,
            data: {
              currentPlan: result.currentPlan,
              newPlan: result.newPlan,
              message: result.message,
              planData: result.planData || planData,
            },
          });
          return;
        }
        if (result.action === 'cancelToFree') {
          setIsBlockingUI(false);
          setCancelToFreeModal({
            isOpen: true,
            data: {
              currentPlan: result.currentPlan,
              newPlan: result.newPlan,
              message: result.message,
            },
          });
          return;
        }
        if (result.action === 'reactivated') {
          await manager.loadUserMembership();
          await refreshUser();
          setIsBlockingUI(false);
          return;
        }
      }
    } catch (error) {
      setIsBlockingUI(false);
      console.error('Plan selection error:', error);
      alert(error.message || 'Unknown error');
    }
  }, [isAuthenticated, manager, refreshUser]);

  const handlePackSelect = useCallback(async (pack) => {
    if (!pack?.id) return;
    if (!isAuthenticated) {
      navigationManager.requireAuth('/pricing', { action: 'purchase_pack', packId: pack.id });
      return;
    }
    setBlockingMessage('Starting credit pack checkout...');
    setIsBlockingUI(true);
    try {
      await manager.startPackCheckout(pack.id);
    } catch (error) {
      setIsBlockingUI(false);
      console.error('Pack checkout error:', error);
      alert(error.message || 'Unknown error');
    }
  }, [isAuthenticated, manager]);

  // Handle incoming payload from other sections
  useEffect(() => {
    if (isInitialized && payload) {
      manager.handlePayload(payload);
    }
  }, [isInitialized, payload, manager]);

  // Handle auth return: user logged in after clicking purchase, resume checkout
  useEffect(() => {
    const state = location.state;
    if (!isInitialized || !state?.action) return;
    if (state.action === 'purchase_plan' && state.planData) {
      const planData = state.planData;
      navigate(location.pathname, { replace: true, state: undefined });
      handlePlanSelect(planData);
      return;
    }
    if (state.action === 'purchase_pack' && state.packId) {
      const packId = state.packId;
      navigate(location.pathname, { replace: true, state: undefined });
      if (!isAdminUser) return;
      const pack = manager.getCreditPacks().find((p) => p.id === packId) || { id: packId };
      handlePackSelect(pack);
    }
  }, [isInitialized, location.state, location.pathname, navigate, handlePlanSelect, handlePackSelect, manager, isAdminUser]);

  // Stripe redirect after credit pack purchase: finalize via same route as plans/products
  const packSessionId = searchParams.get('session_id') || '';
  const packSuccessFlag = searchParams.get('pack_success') === '1';
  const packCheckoutFetchedRef = useRef(new Set());

  useEffect(() => {
    if (!isInitialized || !packSuccessFlag || !packSessionId || !user) return;
    if (packCheckoutFetchedRef.current.has(packSessionId)) return;
    packCheckoutFetchedRef.current.add(packSessionId);

    fetch(`/api/stripe/checkout-session/${encodeURIComponent(packSessionId)}`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then(async (data) => {
        if (data.success && data.session?.payment_status === 'paid') {
          // Await auth refresh so header `membershipDetails` (credits) updates before we strip the URL.
          await refreshUser?.();
          await refreshCredits?.();
          navigate('/pricing', { replace: true });
        }
      })
      .catch((err) => {
        console.error('Failed to finalize credit pack checkout:', err);
      });
  }, [
    isInitialized,
    packSuccessFlag,
    packSessionId,
    user,
    navigate,
    refreshUser,
    refreshCredits,
  ]);

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--pricing">
        <div className="pricing-container">
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            Loading...
          </div>
        </div>
      </MainLayout>
    );
  }

  const plans = manager
    .getPlans()
    .filter((p) => PLAN_IDS.includes((p.uniqueId || '').toLowerCase()))
    .sort((a, b) => {
      const aIdx = PLAN_IDS.indexOf((a.uniqueId || '').toLowerCase());
      const bIdx = PLAN_IDS.indexOf((b.uniqueId || '').toLowerCase());
      return aIdx - bIdx;
    });

  const handleManageSubscription = async () => {
    setBlockingMessage('Opening billing portal...');
    setIsBlockingUI(true);
    try {
      await manager.openCustomerPortal();
    } catch (error) {
      console.error('Customer portal error:', error);
      alert(`Error opening customer portal: ${error.message || 'Unknown error'}`);
    } finally {
      setIsBlockingUI(false);
    }
  };

  return (
    <MainLayout contentClassName="main-content--pricing">
      <div className="pricing-container">
        {isBlockingUI && (
          <div className="pricing-blocking-overlay" aria-busy="true" aria-live="polite">
            <div className="pricing-blocking-overlay__panel">
              <div className="loading-spinner pricing-blocking-overlay__spinner" />
              <div className="pricing-blocking-overlay__text">{blockingMessage}</div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="pricing-header">
          <h1 className="pricing-title">Choose Your Plan</h1>
          <p className="pricing-subtitle">
            Select the perfect plan for your needs. All plans include our core features with varying credit allocations.
          </p>
        </div>

        {/* Plan Cards - Fixed Free, Pro, Max */}
        <div className="pricing-cards-container">
          {manager.getLoading() ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
              Loading plans...
            </div>
          ) : manager.getError() ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#ef4444' }}>
              <p>Failed to load plans: {manager.getError()}</p>
              <button
                onClick={() => manager.loadPlans()}
                style={{
                  marginTop: '16px',
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          ) : plans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
              No plans available at this time.
            </div>
          ) : (
            <div className="pricing-cards-row">
              <ProductCard
                product={localProduct}
                userMembership={manager.getUserMembership()}
                onBuyLocal={(productId) => navigate(`/marketplace/details?productId=${productId}`)}
                onEditClick={() => setLocalCardEditModal(true)}
                showAdminEdit={user?.role === 'admin'}
                linkedProductId={localConfig?.productId || null}
                accessTag={localConfig?.accessTag || null}
                productAccessTags={user?.productAccessTags || []}
              />
              <div className="pricing-cards-divider" aria-hidden="true" />
              {plans.map((plan) => (
                <PlanCard
                  key={plan.uniqueId}
                  plan={plan}
                  onSelect={handlePlanSelect}
                  userMembership={manager.getUserMembership()}
                  onManageSubscription={handleManageSubscription}
                />
              ))}
            </div>
          )}
        </div>

        {/* Credit Packs (one-time credit top-ups) — admin-only */}
        {isAdminUser && manager.getCreditPacks().length > 0 && (
          <div className="credit-packs-section">
            <div className="credit-packs-header">
              <h2 className="credit-packs-title">Need more credits?</h2>
              <p className="credit-packs-subtitle">
                One-time packs to top up your balance. Credits are added instantly after purchase.
              </p>
            </div>
            <div className="credit-packs-row">
              {manager.getCreditPacks().map((pack) => (
                <CreditPackCard key={pack.id} pack={pack} onSelect={handlePackSelect} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Local Card Edit Modal (admin only) */}
      <LocalCardEditModal
        isOpen={localCardEditModal}
        onClose={() => setLocalCardEditModal(false)}
        config={localConfig}
        onSave={async (config) => {
          await manager.saveLocalCardConfig(config);
          await manager.loadLocalCardConfig();
        }}
      />

      {/* Reactivate Subscription Modal */}
      <ReactivateSubscriptionModal
        isOpen={reactivateModal.isOpen}
        onClose={() => setReactivateModal({ isOpen: false, data: null })}
        onConfirm={async () => {
          if (reactivateModal.data) {
            setBlockingMessage('Reactivating subscription...');
            setIsBlockingUI(true);
            try {
              await manager.reactivateSubscription(reactivateModal.data.subscriptionId);
              // Reload user membership to update UI
              await manager.loadUserMembership();
              // Refresh user data to update header (credits, membership, etc.)
              await refreshUser();
            } catch (error) {
              alert(`Failed to reactivate subscription: ${error.message}`);
            } finally {
              setIsBlockingUI(false);
            }
          }
        }}
        message={reactivateModal.data?.message}
      />

      {/* Plan Switch Modal */}
      <PlanSwitchModal
        isOpen={planSwitchModal.isOpen}
        onClose={() => setPlanSwitchModal({ isOpen: false, data: null })}
        onConfirm={async () => {
          if (planSwitchModal.data) {
            setBlockingMessage('Switching plan...');
            setIsBlockingUI(true);
            try {
              await manager.handlePlanSelect(planSwitchModal.data.planData, { confirmPlanSwitch: true });
            } catch (error) {
              alert(`Failed to switch plan: ${error.message}`);
            } finally {
              setIsBlockingUI(false);
            }
          }
        }}
        currentPlan={planSwitchModal.data?.currentPlan}
        newPlan={planSwitchModal.data?.newPlan}
        message={planSwitchModal.data?.message}
      />

      {/* Cancel To Free Modal */}
      <CancelToFreeModal
        isOpen={cancelToFreeModal.isOpen}
        onClose={() => setCancelToFreeModal({ isOpen: false, data: null })}
        onConfirm={async () => {
          setBlockingMessage('Canceling subscription...');
          setIsBlockingUI(true);
          try {
            await manager.cancelToFree();
            await manager.loadUserMembership();
            await refreshUser();
          } catch (error) {
            alert(`Failed to cancel subscription: ${error.message}`);
          } finally {
            setIsBlockingUI(false);
          }
        }}
        currentPlan={cancelToFreeModal.data?.currentPlan}
        newPlan={cancelToFreeModal.data?.newPlan}
        message={cancelToFreeModal.data?.message}
      />

      <style dangerouslySetInnerHTML={{
        __html: `
          .main-content--pricing {
            padding: 0;
          }

          .pricing-container {
            background: radial-gradient(circle at center, rgba(139, 0, 0, 0.15) 0%, #0a0a0a 70%);
            border-radius: 16px;
            position: relative;
          }

          .pricing-header {
            text-align: center;
            margin-bottom: 20px;
            max-width: 900px;
            margin-left: auto;
            margin-right: auto;
          }

          .pricing-title {
            font-size: 3rem;
            font-weight: 300;
            margin-top: 20px;
            margin-bottom: 20px;
            background: linear-gradient(135deg, #ffffff 0%, #ff4444 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .pricing-subtitle {
            font-size: 1.2rem;
            color: #999;
            line-height: 1.6;
          }

          .pricing-cards-container {
            margin: 0 auto;
            padding: 0 40px 40px;
          }

          .pricing-cards-row {
            display: flex;
            justify-content: center;
            align-items: stretch;
            gap: 24px;
            flex-wrap: nowrap;
            width: fit-content;
            margin: 0 auto;
            /* Scale row proportionally when viewport is too narrow to fit all four cards at
               natural size. Intrinsic width ~1353px (4×320 + 3×24 + 1). Available width =
               100vw minus main-content padding (2×16px) and container padding (2×40px).
               Dividing px by px yields a unitless ratio, which is what zoom requires. */
            zoom: min(1, calc((100vw - 112px) / 1353px));
          }

          .pricing-cards-divider {
            width: 1px;
            min-height: 400px;
            flex-shrink: 0;
            /* Same red-glow-in-middle concept as ProductDetailsView horizontal separators */
            background: linear-gradient(
              to bottom,
              transparent 0%,
              rgba(255, 68, 68, 0.8) 25%,
              rgba(255, 68, 68, 0.8) 75%,
              transparent 100%
            );
          }

          @media (max-width: 768px) {
            .pricing-cards-row {
              flex-direction: column;
              align-items: center;
              zoom: 1;
            }
            .pricing-cards-divider {
              display: none;
            }
          }

          .pricing-blocking-overlay {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 0, 0, 0.55);
            backdrop-filter: blur(2px);
            z-index: 99999;
          }

          .pricing-blocking-overlay__panel {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 14px 16px;
            border-radius: 10px;
            border: 1px solid rgba(255, 255, 255, 0.12);
            background: rgba(0, 0, 0, 0.55);
            color: #fff;
          }

          .pricing-blocking-overlay__spinner {
            width: 22px;
            height: 22px;
          }

          .pricing-blocking-overlay__text {
            font-size: 13px;
            color: rgba(255, 255, 255, 0.9);
          }

          .credit-packs-section {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px 40px 60px;
          }

          .credit-packs-header {
            text-align: center;
            margin-bottom: 24px;
          }

          .credit-packs-title {
            font-size: 2rem;
            font-weight: 300;
            margin: 0 0 8px 0;
            color: #fff;
          }

          .credit-packs-subtitle {
            font-size: 1rem;
            color: #999;
            margin: 0;
          }

          .credit-packs-row {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: stretch;
            gap: 20px;
          }

          @media (max-width: 640px) {
            .credit-packs-row {
              flex-direction: column;
              align-items: center;
            }
          }
        `
      }} />
    </MainLayout>
  );
}
